/**
 * The phone app's push registration — asked of the shell, saved to the API.
 *
 * One function for the two callers: `NativePushAuto` (the app asks by itself
 * as soon as somebody is signed in — the user, 2026-10-07: "the app should ask
 * to allow notifications automatically") and the account page's switch.
 *
 * Every failure comes back NAMED (`kind`), so the switch can say it: the
 * phone's refusal, the phone failing to get an address (its own words), no
 * answer from the shell, the server refusing.
 */
import { api } from './api';
import { nativePushToken } from './nativeBridge';
import { readJson, writeJson } from './storage';

export type NativePushResult =
  | { ok: true; token: string }
  | { ok: false; kind: 'noanswer' | 'denied' | 'error' | 'server'; message?: string };

/** Fired on `window` whenever the registration changes, so an open switch redraws. */
export const NATIVE_PUSH_EVENT = 'myrave:native-push';

/** The token this phone registered, if any (`myrave:platform:nativePush`). */
export const nativePushRegistered = (): string | null => readJson<string | null>(null, 'nativePush', null);

/** The person turned alerts off here: the app stops asking by itself. */
export const nativePushTurnedOff = (): boolean => readJson<boolean>(null, 'nativePushOff', false);

function changed(token: string | null): void {
  writeJson(null, 'nativePush', token);
  window.dispatchEvent(new CustomEvent(NATIVE_PUSH_EVENT));
}

export async function registerNativePush(): Promise<NativePushResult> {
  const answer = await nativePushToken();
  if (!answer) return { ok: false, kind: 'noanswer' };
  if (answer.type === 'push.denied') {
    changed(null);
    return answer.reason === 'error'
      ? { ok: false, kind: 'error', ...(answer.message ? { message: answer.message } : {}) }
      : { ok: false, kind: 'denied' };
  }
  if (answer.type !== 'push.token') return { ok: false, kind: 'noanswer' };
  try {
    await api.put('/me/push/native', { token: answer.token, platform: answer.platform });
  } catch (err) {
    return { ok: false, kind: 'server', message: err instanceof Error ? err.message : String(err) };
  }
  writeJson(null, 'nativePushOff', false);
  changed(answer.token);
  return { ok: true, token: answer.token };
}

export async function unregisterNativePush(): Promise<void> {
  const token = nativePushRegistered();
  if (token) await api.del('/me/push/native', { token }).catch(() => undefined);
  writeJson(null, 'nativePushOff', true);
  changed(null);
}
